/**
 * /academy/home for German (D-118): the Goethe-Zertifikat levels A1 to C2. The learner chooses the
 * level to prepare (several are possible), the placement or the project suggests one; for the
 * active level: date, centre, modules followed (B1 to C2 are modular), the one next step, the
 * mock exams (each is the complete official exam of the level; B1 to C2: the modules chosen),
 * the latest result of each module on the official scale, the structure of the level, the German
 * course and « Méthode et astuces Goethe ». Every result is an estimate.
 */

import {
  ApiError,
  getAcademyAttemptsListQueryKey,
  useAcademyAttemptsCreate,
  useAcademyAttemptsList,
  useAcademyMocksCreate,
  useAcademyMocksList,
  useAcademyProgressRetrieve,
} from "@agencyos/api-client";
import {
  GOETHE_LEVELS,
  GOETHE_MODULES,
  type GoetheCatalogLevel,
  type GoetheLevel,
  type GoetheSkill,
  type GoetheState,
  formatPoints,
  goetheNextStep,
  pickLabel,
  sittingStep,
} from "@agencyos/core";
import { Badge, Button, GlassCard, SelectField, Skeleton, TextField, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, ClipboardList, Clock, Compass, Dumbbell, Flag, MapPin, Play, Route, Target, Users } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { fieldErrors } from "../../lib/settings";
import { MethodHomeEntry } from "../ielts/CourseEntries";
import { useCoursePath } from "../ielts/CoursePage";
import { HomeExtras } from "../coaching/HomeExtras";
import { RenewalBanner } from "../sales/RenewalBanner";
import { dateText, daysUntil } from "../shared";
import { isObject } from "../types";
import { CardTitle, PageHead, SectionTitle, SkillIcon } from "../ui";
import { UrgentMocks } from "../UrgentMocks";
import { ExamDatesCard } from "../ExamDatesCard";
import { GOETHE, useGoethe, useGoetheFormat, useGoetheSave } from "./useGoethe";
import "./goethe.css";
import { MotherTonguePrompt } from "../ielts/MotherTongue";

/** The six levels: official name, modular or not, written minutes; prepare one. */
export function LevelPicker({ state, onChosen }: { state: GoetheState; onChosen?: () => void }) {
  const { t } = useTranslation();
  const save = useGoetheSave();
  const prepared = new Set(state.levels.map((l) => l.level));
  const suggested = state.placementLevel || state.recommendedLevel;
  const choose = (level: GoetheLevel) => save.mutate({ data: { level } }, { onSuccess: () => onChosen?.() });
  return (
    <ul className="ac-goethe-levels" aria-label={t("goethe.levels.title")}>
      {state.catalog.map((row: GoetheCatalogLevel) => {
        const active = state.activeLevel === row.level;
        return (
          <li key={row.level} className={`ac-goethe-level ac-surface${active ? " ac-goethe-level--active" : ""}`}>
            <span className="ac-goethe-level__badge ao-num" aria-hidden="true">
              {row.level}
            </span>
            <span className="ac-goethe-level__name">{row.officialName}</span>
            <span className="ac-goethe-level__facts">
              <Badge tone={row.modular ? "info" : "default"}>{t(row.modular ? "goethe.levels.modular" : "goethe.levels.whole")}</Badge>
              <span className="ac-caption ao-num">
                <Clock aria-hidden size={12} /> {t("goethe.levels.written", { count: row.writtenMinutes })}
              </span>
              <span className="ac-caption">
                <Users aria-hidden size={12} /> {t(`goethe.setting.${row.setting || "pair"}`)}
              </span>
            </span>
            <span className="ac-goethe-level__tags">
              {suggested === row.level && <Badge tone="success">{t("goethe.levels.suggested")}</Badge>}
              {prepared.has(row.level) && !active && <Badge>{t("goethe.levels.prepared")}</Badge>}
              {(state.mocks[row.level] ?? 0) > 0 && <span className="ac-caption ao-num">{t("goethe.levels.mocks", { count: state.mocks[row.level] ?? 0 })}</span>}
            </span>
            {active ? (
              <span className="ac-goethe-level__current">
                <CheckCircle2 aria-hidden size={15} /> {t("goethe.levels.active")}
              </span>
            ) : (
              <Button size="sm" variant={suggested === row.level ? "primary" : "secondary"} disabled={save.isPending} onClick={() => choose(row.level)}>
                {prepared.has(row.level) ? t("goethe.levels.switch") : t("goethe.levels.prepare")}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The project (work visa, studies, family reunion, Chancenkarte…) and the level usually asked. */
function ProjectCard({ state }: { state: GoetheState }) {
  const { t, i18n } = useTranslation();
  const save = useGoetheSave();
  const current = state.projects.find((p) => p.key === state.project);
  return (
    <GlassCard as="section" className="ac-card" aria-labelledby="goethe-project">
      <CardTitle id="goethe-project" icon={<Flag size={16} />} tone="info">
        {t("goethe.project.title")}
      </CardTitle>
      <SelectField
        label={t("goethe.project.label")}
        value={state.project}
        onChange={(e) => save.mutate({ data: { project: e.target.value } })}
        options={[{ value: "", label: t("goethe.project.none") }, ...state.projects.map((p) => ({ value: p.key, label: pickLabel(p.label, i18n.language) }))]}
      />
      {current && (
        <p className="ac-note" role="note">
          {current.level ? <strong>{t("goethe.project.level", { level: current.level })} </strong> : null}
          {pickLabel(current.hint, i18n.language)}
        </p>
      )}
      <p className="ac-footnote">{pickLabel(state.projectSource, i18n.language) || t("goethe.project.check")}</p>
    </GlassCard>
  );
}

/** Date, centre, target and modules of the active level. */
function LevelGoalForm({ state, onDone }: { state: GoetheState; onDone: () => void }) {
  const { t } = useTranslation();
  const save = useGoetheSave();
  const level = state.activeLevel as GoetheLevel;
  const row = state.levels.find((l) => l.level === level);
  const catalog = state.catalog.find((c) => c.level === level);
  const [examDate, setExamDate] = useState(row?.examDate ?? "");
  const [center, setCenter] = useState(row?.examCenter ?? "");
  const [target, setTarget] = useState(String(row?.target ?? 60));
  const [modules, setModules] = useState<GoetheSkill[]>(row?.modules?.length ? row.modules : (catalog?.order ?? []));
  const errors = fieldErrors(save.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      { data: { level, exam_date: examDate || null, exam_center: center, target: Number(target), modules } },
      { onSuccess: onDone },
    );
  };
  const toggle = (skill: GoetheSkill) => setModules((list) => (list.includes(skill) ? list.filter((s) => s !== skill) : [...list, skill]));
  return (
    <GlassCard as="section" className="ac-card ac-card--accent" aria-labelledby="goethe-goal-form">
      <form className="ac-form" onSubmit={onSubmit}>
        <CardTitle id="goethe-goal-form" icon={<Target size={16} />} tone="warning">
          {t("goethe.goal.title", { level })}
        </CardTitle>
        <div className="ac-form-grid">
          <TextField label={t("academy.goals.examDate")} type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} error={errors.exam_date} />
          <SelectField
            label={t("goethe.goal.center")}
            hint={t("goethe.goal.centerHint")}
            value={center}
            onChange={(e) => setCenter(e.target.value)}
            options={[{ value: "", label: t("academy.goals.choose") }, ...state.centers.map((c) => ({ value: c.code, label: [c.city, c.name].filter(Boolean).join(" · ") }))]}
            error={errors.exam_center}
          />
          <SelectField
            label={t("goethe.goal.target")}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            options={[60, 70, 80, 90].map((n) => ({ value: String(n), label: t("goethe.goal.targetValue", { n }) }))}
            error={errors.target}
          />
        </div>
        {catalog?.modular && (
          <fieldset className="ac-goethe-modules">
            <legend>{t("goethe.goal.modules")}</legend>
            {catalog.order.map((skill) => (
              <label key={skill} className="ac-check">
                <input type="checkbox" checked={modules.includes(skill)} onChange={() => toggle(skill)} />
                <span>{GOETHE_MODULES[skill]}</span>
              </label>
            ))}
            <p className="ac-footnote">{t("goethe.goal.modulesHint")}</p>
          </fieldset>
        )}
        <div className="ac-card__actions">
          <Button variant="ghost" onClick={onDone}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t("common.loading") : t("academy.goals.save")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

interface ModuleFigure {
  points: number | null;
  max: number | null;
  percent: number | null;
  rating: string;
}

function moduleFigures(value: unknown, language: string): Record<string, ModuleFigure> {
  const analysis = isObject(value) && isObject(value.analysis) ? value.analysis : {};
  const modules = isObject(analysis.modules) ? analysis.modules : {};
  const out: Record<string, ModuleFigure> = {};
  for (const skill of ["co", "ce", "ee", "eo"]) {
    const row = isObject(modules[skill]) ? modules[skill] : {};
    const rating = isObject(row.rating) ? row.rating : {};
    out[skill] = {
      points: typeof row.points === "number" ? row.points : null,
      max: typeof row.max === "number" ? row.max : null,
      percent: typeof row.percent === "number" ? row.percent : null,
      rating: String((language.startsWith("en") ? rating.en : rating.fr) ?? rating.de ?? ""),
    };
  }
  return out;
}

/** The mocks of the active level; B1 to C2: the modules to sit (like the official exam). */
function LevelMocks({ level, modular, order, locked }: { level: GoetheLevel; modular: boolean; order: GoetheSkill[]; locked: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mocks = useAcademyMocksList({ exam: GOETHE, level }, { query: { retry: false } });
  const create = useAcademyMocksCreate();
  const practice = useAcademyAttemptsCreate();
  const [skills, setSkills] = useState<GoetheSkill[]>(order);
  const rows = mocks.data?.status === 200 ? mocks.data.data : [];
  const go = (id: string) => {
    void queryClient.invalidateQueries({ queryKey: getAcademyAttemptsListQueryKey({ exam: GOETHE, level }) });
    navigate(`/academy/attempt/${id}`);
  };
  const start = (mock: string) =>
    create.mutate({ data: { mock, skills: modular ? skills : undefined } }, { onSuccess: (r) => r.status === 201 && go(r.data.id) });
  const train = (skill: GoetheSkill) =>
    practice.mutate(
      { data: { exam: GOETHE, level, kind: "practice", mode: "training", skills: [skill] } },
      { onSuccess: (r) => r.status === 201 && go(r.data.id) },
    );
  const error = create.error ?? practice.error;
  const errorText = error instanceof ApiError ? (error.detail ?? t("common.error")) : error ? t("common.error") : "";
  if (mocks.isLoading) return <Skeleton style={{ height: 160 }} />;
  return (
    <section className="ac-section" aria-labelledby="goethe-mocks">
      <SectionTitle id="goethe-mocks">{t("goethe.mocks.title", { level })}</SectionTitle>
      <p className="ac-secondary">{t(modular ? "goethe.mocks.modularText" : "goethe.mocks.wholeText")}</p>
      {modular && (
        <fieldset className="ac-goethe-modules" aria-label={t("goethe.mocks.modules")}>
          {order.map((skill) => (
            <label key={skill} className="ac-check">
              <input type="checkbox" checked={skills.includes(skill)} onChange={() => setSkills((l) => (l.includes(skill) ? l.filter((s) => s !== skill) : [...l, skill]))} />
              <span>{GOETHE_MODULES[skill]}</span>
            </label>
          ))}
        </fieldset>
      )}
      {rows.length === 0 ? (
        <p className="ac-note">{t("goethe.mocks.none")}</p>
      ) : (
        <ul className="ac-goethe-mocks">
          {rows.map((mock) => {
            const best = isObject(mock.best) ? mock.best : null;
            return (
              <li key={mock.id} className="ac-goethe-mock ac-surface">
                <span className="ac-goethe-mock__n ao-num" aria-hidden="true">
                  {mock.number}
                </span>
                <span className="ac-goethe-mock__text">
                  <strong>{mock.title}</strong>
                  <span className="ac-caption">{mock.session_theme}</span>
                  {best && typeof best.mean === "number" && <span className="ac-caption ao-num">{t("goethe.mocks.best", { percent: best.mean })}</span>}
                </span>
                <Button size="sm" icon={<Play aria-hidden size={14} />} disabled={locked || create.isPending || (modular && !skills.length)} onClick={() => start(mock.id)}>
                  {mock.done ? t("goethe.mocks.again") : t("goethe.mocks.start")}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="ac-goethe-practice" role="group" aria-label={t("goethe.mocks.practice")}>
        <span className="ac-caption">
          <Dumbbell aria-hidden size={13} /> {t("goethe.mocks.practice")}
        </span>
        {order.map((skill) => (
          <Button key={skill} size="sm" variant="ghost" disabled={locked || practice.isPending || !rows.length} onClick={() => train(skill)}>
            <SkillIcon skill={skill} size={14} /> {GOETHE_MODULES[skill]}
          </Button>
        ))}
      </div>
      {errorText && <p className="ko">{errorText}</p>}
      {locked && <p className="ac-footnote">{t("goethe.home.needPlan")}</p>}
    </section>
  );
}

export function GoetheHome() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state, loading } = useGoethe();
  const level = state?.activeLevel || "";
  const [editing, setEditing] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const progress = useAcademyProgressRetrieve({ exam: GOETHE, ...(level ? { level } : {}) }, { query: { retry: false, enabled: Boolean(level) } });
  const attempts = useAcademyAttemptsList({ exam: GOETHE }, { query: { retry: false } });
  const { path } = useCoursePath(Boolean(state?.hasAccess), GOETHE);
  const format = useGoetheFormat(level);
  const create = useAcademyAttemptsCreate();
  const figures = useMemo(() => moduleFigures(progress.data?.status === 200 ? progress.data.data : null, i18n.language), [progress.data, i18n.language]);

  if (loading) return <Skeleton style={{ height: 320 }} />;
  if (!state) return <p className="ko">{t("academy.player.networkError")}</p>;

  const list = attempts.data?.status === 200 ? attempts.data.data : [];
  const catalog = state.catalog.find((c) => c.level === level);
  const row = state.levels.find((l) => l.level === level);
  const step = goetheNextStep(state, { courseNext: path?.nextLesson ?? null, mocksDone: list.filter((a) => a.kind === "mock" && a.status === "submitted").length });
  // D-160: a paused mock exam never hides (nor stands for) the placement test
  const sitting = sittingStep(list, step !== "placement" && step !== "choose_level");
  const running = sitting?.step === "resume" ? sitting.open : undefined;
  const placementOpen = sitting?.step === "placement" ? sitting.open : undefined;
  const otherOpen = list.find((a) => a.status === "in_progress" && a.kind !== "placement");
  const days = daysUntil(row?.examDate ?? null);
  const startPlacement = () =>
    create.mutate(
      { data: { exam: GOETHE, kind: "placement", skills: ["ce"], mode: "exam" } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          void queryClient.invalidateQueries({ queryKey: getAcademyAttemptsListQueryKey({ exam: GOETHE }) });
          navigate(`/academy/attempt/${response.data.id}`);
        },
      },
    );
  const sections = isObject(format.view) && isObject(format.view.sections) ? format.view.sections : {};

  return (
    <div className="ac-page ac-goethe" lang={i18n.language}>
      <PageHead eyebrow={t("ielts.exams.goethe.name")} title={t("goethe.home.title")} subtitle={<p className="ac-note" role="note">{t("goethe.estimateLong")}</p>} />

      {(!level || choosing) && (
        <section className="ac-section" aria-labelledby="goethe-levels">
          <SectionTitle id="goethe-levels">{t("goethe.levels.title")}</SectionTitle>
          <p className="ac-secondary">{t("goethe.levels.hint")}</p>
          <LevelPicker state={state} onChosen={() => setChoosing(false)} />
        </section>
      )}

      {level && (editing ? (
        <LevelGoalForm state={state} onDone={() => setEditing(false)} />
      ) : (
        <GlassCard as="section" className="ac-card ac-goal" aria-labelledby="goethe-goal">
          <div className="ac-card__head">
            <CardTitle id="goethe-goal" icon={<Target size={16} />} tone="warning">
              {catalog?.officialName ?? level}
            </CardTitle>
            <span className="ac-card__tools">
              <Button variant="ghost" size="sm" onClick={() => setChoosing((v) => !v)}>
                {t("goethe.levels.change")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                {t("academy.goals.edit")}
              </Button>
            </span>
          </div>
          <div className="ac-goal__figures">
            <div className="ac-goal__figure">
              <span className="ac-goal__label">{t("goethe.goal.target")}</span>
              <span className="ac-goal__value ao-num">{t("goethe.goal.targetValue", { n: row?.target ?? 60 })}</span>
            </div>
            <div className="ac-goal__figure">
              <span className="ac-goal__label">{t("academy.goals.examDate")}</span>
              <span className="ac-goal__value">{row?.examDate ? dateText(row.examDate, i18n.language) : "—"}</span>
              {days !== null && days >= 0 && <span className="ac-caption">{t("goethe.home.daysLeft", { count: days })}</span>}
            </div>
            <div className="ac-goal__figure">
              <span className="ac-goal__label">
                <MapPin aria-hidden size={13} /> {t("goethe.goal.center")}
              </span>
              <span className="ac-goal__value">{row?.examCenter || "—"}</span>
            </div>
          </div>
        </GlassCard>
      ))}

      <GlassCard as="section" className="ac-card ac-next" aria-labelledby="goethe-next">
        <CardTitle id="goethe-next" icon={<Compass size={16} />} tone="info">
          {t("goethe.home.next")}
        </CardTitle>
        <div className="ac-card__actions ac-card__actions--start">
          {running ? (
            <>
              <p className="ac-secondary">{t("goethe.home.resumeText")}</p>
              <Link className={buttonClass("primary")} to={`/academy/attempt/${running.id}`}>
                <Play aria-hidden size={16} /> {t("academy.history.resume")}
              </Link>
            </>
          ) : placementOpen ? (
            <>
              <p className="ac-secondary">{t("academy.placement.inProgress")}</p>
              <Link className={buttonClass("primary")} to={`/academy/attempt/${placementOpen.id}`}>
                <Play aria-hidden size={16} /> {t("academy.placement.resume")}
              </Link>
            </>
          ) : step === "placement" || step === "choose_level" ? (
            <>
              <p className="ac-secondary">{step === "placement" ? t("goethe.home.placementText") : t("goethe.home.chooseText", { level: state.placementLevel || state.recommendedLevel })}</p>
              <Button icon={<Play aria-hidden size={16} />} disabled={create.isPending} onClick={startPlacement}>
                {create.isPending ? t("common.loading") : t("goethe.home.placement")}
              </Button>
              {otherOpen && (
                <Link className={buttonClass("ghost")} to={`/academy/attempt/${otherOpen.id}`}>
                  {t("academy.ui.home.resumeTitle", { kind: t(`academy.kind.${otherOpen.kind}`) })}
                </Link>
              )}
            </>
          ) : step === "plans" ? (
            <>
              <p className="ac-secondary">{t("goethe.home.needPlan")}</p>
              <Link className={buttonClass("primary")} to="/academy/plans">
                {t("goethe.home.plans")} <ArrowRight aria-hidden size={16} />
              </Link>
            </>
          ) : (
            <>
              <p className="ac-secondary">{t("goethe.home.courseText", { done: path?.completed ?? 0, total: path?.total ?? 0 })}</p>
              <Link className={buttonClass(step === "course" ? "primary" : "secondary")} to="/academy/course">
                <Route aria-hidden size={16} /> {t("goethe.home.course")}
              </Link>
              <a className={buttonClass(step === "mock" ? "primary" : "secondary")} href="#goethe-mocks">
                <ClipboardList aria-hidden size={16} /> {t("goethe.home.mocks")}
              </a>
            </>
          )}
        </div>
      </GlassCard>

      <ProjectCard state={state} />
      <RenewalBanner />
      <HomeExtras />
      <MotherTonguePrompt />
      {level && <UrgentMocks locked={!state.hasAccess} />}
      {level && <ExamDatesCard exam="goethe" level={level} />}

      {level && (
        <section className="ac-section" aria-labelledby="goethe-results">
          <SectionTitle id="goethe-results">{t("goethe.home.levels", { level })}</SectionTitle>
          <ul className="ac-tef-levels">
            {(catalog?.order ?? []).map((skill) => {
              const figure = figures[skill];
              return (
                <li key={skill} className={`ac-tef-level ac-surface ac-skill--${skill}`}>
                  <SkillIcon skill={skill} size={16} />
                  <span className="ac-tef-level__name">{GOETHE_MODULES[skill]}</span>
                  <span className="ac-tef-level__value ao-num">
                    {figure?.points !== null && figure?.points !== undefined ? `${formatPoints(figure.points, i18n.language)} / ${formatPoints(figure.max, i18n.language)}` : "—"}
                  </span>
                  <span className="ac-caption">{figure?.rating || ""}</span>
                </li>
              );
            })}
          </ul>
          <p className="ac-footnote">{t(catalog?.modular ? "goethe.home.modularNote" : "goethe.home.wholeNote")}</p>
        </section>
      )}

      {level && catalog && <LevelMocks key={level} level={level} modular={catalog.modular} order={catalog.order} locked={!state.hasAccess} />}

      {level && catalog && (
        <section className="ac-section" aria-labelledby="goethe-structure">
          <SectionTitle id="goethe-structure">{t("goethe.home.structure", { level })}</SectionTitle>
          <ol className="ac-structure">
            {catalog.order.map((skill, index) => {
              const module = catalog.modules[skill];
              const section = isObject(sections[skill]) ? sections[skill] : {};
              const parts = Array.isArray(section.parts) ? section.parts.length : isObject(section.tasks) ? Object.keys(section.tasks).length : module?.parts ?? 0;
              return (
                <li key={skill} className={`ac-structure__item ac-skill--${skill}`}>
                  <SkillIcon skill={skill} size={16} />
                  <span className="ac-structure__text">
                    <strong>
                      <span className="ac-structure__step" aria-hidden="true">
                        {index + 1}
                      </span>
                      {GOETHE_MODULES[skill]} · {pickLabel(module?.label, i18n.language)}
                    </strong>
                    <span className="ac-structure__facts">
                      <span>{t("goethe.home.parts", { count: parts })}</span>
                      <span className="ac-structure__time">
                        <Clock aria-hidden size={13} /> {t("academy.mocks.minutes", { count: module?.minutes ?? 0 })}
                      </span>
                      <span className="ao-num">{t("goethe.home.points", { max: formatPoints(module?.pointsMax ?? 0, i18n.language) })}</span>
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
          <p className="ac-footnote">
            <Badge>{t("goethe.home.organizer")}</Badge> {t("goethe.home.structureNote")}
          </p>
        </section>
      )}

      <MethodHomeEntry exam={GOETHE} />
      <p className="ac-footnote ac-goethe-levels-all">{GOETHE_LEVELS.join(" · ")}</p>
    </div>
  );
}
